import { Color, Engine, Material } from "excalibur";

export const extractFrameFromSpriteSheet = (
  image: HTMLImageElement,
  dimensions: { x: number; y: number; width: number; height: number }
): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  ctx.drawImage(
    image,
    dimensions.x,
    dimensions.y,
    dimensions.width,
    dimensions.height,
    0,
    0,
    dimensions.width,
    dimensions.height
  );
  return canvas;
};

export const createTintedMaterial = (
  engine: Engine,
  tintColor: Color
): Material => {
  const [r, g, b] = [tintColor.r / 255, tintColor.g / 255, tintColor.b / 255]; // Normalize color to 0-1

  return engine.graphicsContext.createMaterial({
    name: "custom-material",
    fragmentSource: `#version 300 es
          precision mediump float;

          in vec2 v_uv;
          out vec4 fragColor;
          uniform vec2 u_graphic_resolution;
          uniform sampler2D u_graphic;

          vec2 uv_iq(in vec2 uv, in vec2 texture_size) {
            vec2 pixel = uv * texture_size;
            vec2 seam = floor(pixel + 0.5);
            vec2 dudv = fwidth(pixel);
            pixel = seam + clamp((pixel - seam) / dudv, -0.5, 0.5);
            return pixel / texture_size;
          }

          void main(void) {
              vec2 newUv = uv_iq(v_uv, u_graphic_resolution);
              vec4 sourceColor = texture(u_graphic, newUv);

              // Apply custom tint
              vec3 customTint = vec3(${r.toFixed(3)}, ${g.toFixed(3)}, ${b.toFixed(3)});
              sourceColor.rgb = mix(sourceColor.rgb, customTint, 0.5); // Blend with the tint color

              fragColor = sourceColor;
              fragColor.rgb = fragColor.rgb * fragColor.a; // Premultiply alpha
          }`,
  });
};

export const createFakeCanvas = (
  elements: Array<{
    image: HTMLImageElement;
    dimension: { x: number; y: number; width: number; height: number };
    position: { x: number; y: number };
    tint?: string; // tint as a hex color string
  }>,
  engine: Engine // Add the engine parameter for material creation
): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Failed to get 2D context from canvas.");
  }

  // Set canvas size (adjust according to the total dimensions of elements)
  canvas.width = 100; // Set according to your needs
  canvas.height = 100; // Set according to your needs
  // Draw all elements first
  // elements.forEach(({ image, dimension, position }) => {
  //   const { x, y, width, height } = dimension;

  //   // Draw the original image onto the canvas
  //   context.drawImage(image, x, y, width, height, position.x, position.y, width, height);
  // });

  // Apply tint only to the last element if it has a tint

  const applyTint = (context, image, dimension, position, tint) => {
    const { width, height } = dimension;

    // Save the current canvas state
    context.save();

    // Draw the original image
    context.drawImage(image, dimension.x, dimension.y, width, height, position.x, position.y, width, height);

    // Apply tint using multiply mode
    context.globalCompositeOperation = "multiply"; // This blends the tint with the existing pixels
    context.fillStyle = tint;
    context.fillRect(position.x, position.y, width, height);

    // Reset to normal blending mode
    context.globalCompositeOperation = "destination-atop"; 
    context.drawImage(image, dimension.x, dimension.y, width, height, position.x, position.y, width, height);

    context.restore();
};

const lastElement2 = elements[elements.length - 2];

if (lastElement2.tint) {
    applyTint(context, lastElement2.image, lastElement2.dimension, lastElement2.position, "rgba(255, 255, 0, 0.5)"); // Yellow tint
    context.strokeStyle = "blue";
    context.lineWidth = 2;
    context.strokeRect(lastElement2.position.x, lastElement2.position.y, lastElement2.dimension.width, lastElement2.dimension.height);
  }

const lastElement = elements[elements.length - 1];
if (lastElement.tint) {
    applyTint(context, lastElement.image, lastElement.dimension, lastElement.position, "rgba(255, 0, 0, 0.5)"); // Red tint

}

  return canvas;
};

// Utility function to convert hex color to RGB
const hexToRgb = (hex: string): [number, number, number] => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  // Parse r, g, b values
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return [r, g, b] as [number, number, number]; // Ensure it is treated as a tuple
};
